/**
 * Browser implementation of the pose classifier trainer.
 *
 * Architecture: MoveNet (singlepose lightning) extracts a 17×3 keypoint
 * vector per sample, then a small MLP classifies the vector. Same
 * `ImageTrainer` shape as the image trainer so the store doesn't need
 * to learn a new contract — `predict` runs MoveNet on the live video
 * and feeds the result to the MLP.
 *
 * Real-browser-only — TF.js + pose-detection are dynamically imported
 * so they ship in their own chunks and never inflate the platform shell.
 */

import type { Class, Sample, TrainedLabel } from '../data/types';
import { extractTfjsLayersModelArtifacts } from './extractArtifacts';
import { POSE_VECTOR_LENGTH, detectPoseVector, detectPoseVectorFromVideo } from './moveNet';
import {
  buildExportFilename,
  checkConvergence,
  clampHyperparams,
  TrainingInputError,
  type ExportModelInput,
  type ImageTrainer,
  type LoadedModel,
  type ModelArtifacts,
  type PredictSource,
  type TrainInput,
  type TrainProgress,
  type TrainResult,
} from './trainer';

import { getTf } from './tfBackend';
type Tf = typeof import('@tensorflow/tfjs');

function modelKey(projectId: string): string {
  return `indexeddb://bryta-pose-${projectId}`;
}

const HEAD_HIDDEN_UNITS = 64;

function reportProgress(input: TrainInput, event: TrainProgress): void {
  input.onProgress?.(event);
}

function bucketByClass(input: TrainInput): Map<string, Sample[]> {
  const buckets = new Map<string, Sample[]>();
  for (const sample of input.samples) {
    const bucket = buckets.get(sample.classId);
    if (bucket) bucket.push(sample);
    else buckets.set(sample.classId, [sample]);
  }
  return buckets;
}

function buildLabels(classes: Class[], buckets: Map<string, Sample[]>): TrainedLabel[] {
  return classes
    .filter((cls) => (buckets.get(cls.id)?.length ?? 0) > 0)
    .map((cls) => ({ classId: cls.id, name: cls.name }));
}

export const browserPoseTrainer: ImageTrainer = {
  async train(input) {
    const { signal } = input;
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const params = clampHyperparams(input.hyperparams);

    const tf = await getTf();
    // The "loading-mobilenet" phase is repurposed — for pose, we're
    // loading MoveNet. Keeping the existing label so the store and
    // TrainPanel don't need a parallel pose-only state machine.
    reportProgress(input, { phase: 'loading-mobilenet' });

    const buckets = bucketByClass(input);
    const labels = buildLabels(input.classes, buckets);
    if (labels.length < 2) {
      throw new TrainingInputError('Need at least two classes with samples to train.');
    }

    const totalSamples = input.samples.length;
    const xs: Float32Array[] = [];
    const ys: number[] = [];
    let processed = 0;

    for (let labelIndex = 0; labelIndex < labels.length; labelIndex++) {
      const label = labels[labelIndex]!;
      const samples = buckets.get(label.classId) ?? [];
      for (const sample of samples) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const vector = await detectPoseVector(sample.blob);
        xs.push(vector);
        ys.push(labelIndex);
        processed++;
        reportProgress(input, {
          phase: 'extracting',
          current: processed,
          total: totalSamples,
        });
      }
    }
    if (xs.length === 0) {
      throw new TrainingInputError('No usable samples were found.');
    }

    const xData = new Float32Array(xs.length * POSE_VECTOR_LENGTH);
    for (let i = 0; i < xs.length; i++) {
      xData.set(xs[i]!, i * POSE_VECTOR_LENGTH);
    }
    const xTensor = tf.tensor2d(xData, [xs.length, POSE_VECTOR_LENGTH]);
    const yTensor = tf.oneHot(tf.tensor1d(ys, 'int32'), labels.length);

    const model = tf.sequential();
    model.add(
      tf.layers.dense({
        inputShape: [POSE_VECTOR_LENGTH],
        units: HEAD_HIDDEN_UNITS,
        activation: 'relu',
      }),
    );
    model.add(tf.layers.dropout({ rate: 0.3 }));
    model.add(tf.layers.dense({ units: labels.length, activation: 'softmax' }));
    model.compile({
      optimizer: tf.train.adam(params.learningRate),
      loss: 'categoricalCrossentropy',
      metrics: ['accuracy'],
    });

    let aborted = false;
    const onAbort = (): void => {
      aborted = true;
      model.stopTraining = true;
    };
    signal?.addEventListener('abort', onAbort);

    const lossHistory: number[] = [];
    let lastAccuracy = 0;

    try {
      await model.fit(xTensor, yTensor, {
        epochs: params.epochs,
        batchSize: params.batchSize,
        shuffle: true,
        callbacks: {
          onEpochEnd: (epoch, logs) => {
            const loss = logs?.loss ?? 0;
            const accuracy = logs?.acc ?? logs?.accuracy ?? 0;
            lossHistory.push(loss);
            lastAccuracy = accuracy;
            reportProgress(input, {
              phase: 'training',
              epoch: epoch + 1,
              epochs: params.epochs,
              loss,
              accuracy,
            });
          },
        },
      });
      if (aborted) throw new DOMException('Aborted', 'AbortError');
      await model.save(modelKey(input.projectId));
    } finally {
      signal?.removeEventListener('abort', onAbort);
      xTensor.dispose();
      yTensor.dispose();
      model.dispose();
    }

    const sampleCounts: Record<string, number> = {};
    for (const label of labels) {
      sampleCounts[label.classId] = buckets.get(label.classId)?.length ?? 0;
    }
    const finalLoss = lossHistory[lossHistory.length - 1] ?? 0;
    const { converged } = checkConvergence(lossHistory);

    const result: TrainResult = {
      labels,
      sampleCounts,
      lossHistory,
      finalLoss,
      finalAccuracy: lastAccuracy,
      converged,
    };
    return result;
  },

  async removeModel(projectId) {
    try {
      const tf = await getTf();
      await tf.io.removeModel(modelKey(projectId));
    } catch (err) {
      if (err instanceof Error && /not.*find/i.test(err.message)) return;
      console.warn('removeModel (pose) failed:', err);
    }
  },

  async loadModel(projectId) {
    const tf = await getTf();
    try {
      const head = await tf.loadLayersModel(modelKey(projectId));
      return { head, tf } as LoadedModel;
    } catch {
      return null;
    }
  },

  async predict(loaded, source) {
    const { head, tf } = loaded as {
      head: Awaited<ReturnType<Tf['loadLayersModel']>>;
      tf: Tf;
    };
    const vector = await poseVectorFromSource(source);
    const xTensor = tf.tensor2d(vector, [1, POSE_VECTOR_LENGTH]);
    try {
      const probs = head.predict(xTensor) as ReturnType<Tf['tensor']>;
      const data = await probs.data();
      probs.dispose();
      return Array.from(data as Float32Array);
    } finally {
      xTensor.dispose();
    }
  },

  disposeModel(loaded) {
    const { head } = loaded as { head: { dispose(): void } };
    try {
      head.dispose();
    } catch {
      // Already disposed; no-op.
    }
  },

  async extractArtifacts(input: ExportModelInput): Promise<ModelArtifacts> {
    return extractTfjsLayersModelArtifacts({
      modelKey: modelKey(input.projectId),
      input,
      extras: { brytaModelKind: 'pose' },
    });
  },

  async exportModel(input: ExportModelInput) {
    // Same artefact shape as the image trainer's export — a TF.js
    // LayersModel zip. The downstream JS shim that drives MoveNet
    // is shipped separately and lands in Phase 4 with the export
    // dialog overhaul.
    const tf = await getTf();
    const { zipSync } = await import('fflate');
    let head: Awaited<ReturnType<Tf['loadLayersModel']>>;
    try {
      head = await tf.loadLayersModel(modelKey(input.projectId));
    } catch {
      throw new Error('No trained pose model was found to export.');
    }
    try {
      head.setUserDefinedMetadata({
        brytaProjectName: input.projectName,
        brytaLabels: input.labels,
        brytaSchemaVersion: 1,
        brytaModelKind: 'pose',
      });

      let captured: import('@tensorflow/tfjs').io.ModelArtifacts | null = null;
      const saveHandler: import('@tensorflow/tfjs').io.IOHandler = {
        save: async (artifacts) => {
          captured = artifacts;
          return {
            modelArtifactsInfo: {
              dateSaved: new Date(),
              modelTopologyType: 'JSON',
            },
          };
        },
      };
      await head.save(saveHandler);
      if (!captured) throw new Error('TF.js did not produce model artifacts.');
      const artifacts = captured as import('@tensorflow/tfjs').io.ModelArtifacts;

      const modelJson = {
        modelTopology: artifacts.modelTopology,
        format: artifacts.format ?? 'layers-model',
        generatedBy: 'bryta-learn',
        convertedBy: null,
        weightsManifest: [
          {
            paths: ['./weights.bin'],
            weights: artifacts.weightSpecs ?? [],
          },
        ],
        userDefinedMetadata: artifacts.userDefinedMetadata ?? {
          brytaProjectName: input.projectName,
          brytaLabels: input.labels,
          brytaSchemaVersion: 1,
          brytaModelKind: 'pose',
        },
      };
      const metadataJson = {
        brytaProjectName: input.projectName,
        brytaLabels: input.labels,
        brytaModelKind: 'pose',
        brytaSchemaVersion: 1,
        exportedAt: new Date().toISOString(),
      };

      const encoder = new TextEncoder();
      const weightBuffers: ArrayBuffer[] = [];
      if (artifacts.weightData) {
        if (Array.isArray(artifacts.weightData)) {
          for (const buf of artifacts.weightData) weightBuffers.push(buf);
        } else {
          weightBuffers.push(artifacts.weightData);
        }
      }
      const totalWeightBytes = weightBuffers.reduce((sum, b) => sum + b.byteLength, 0);
      const weightData = new Uint8Array(totalWeightBytes);
      let weightOffset = 0;
      for (const buf of weightBuffers) {
        weightData.set(new Uint8Array(buf), weightOffset);
        weightOffset += buf.byteLength;
      }
      const zipped = zipSync({
        'model.json': encoder.encode(JSON.stringify(modelJson, null, 2)),
        'weights.bin': weightData,
        'metadata.json': encoder.encode(JSON.stringify(metadataJson, null, 2)),
      });
      const filename = buildExportFilename(input.projectName);
      const zippedBuffer = zipped.buffer.slice(
        zipped.byteOffset,
        zipped.byteOffset + zipped.byteLength,
      ) as ArrayBuffer;
      const blob = new Blob([zippedBuffer], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filename}-pose.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } finally {
      head.dispose();
    }
  },
};

async function poseVectorFromSource(source: PredictSource): Promise<Float32Array> {
  if (source instanceof HTMLVideoElement) {
    return detectPoseVectorFromVideo(source);
  }
  // Image / canvas / bitmap path: encode to a blob and run the bitmap-
  // based detector. Slow path but rarely hit — predict is overwhelmingly
  // called on the live webcam.
  const canvas = document.createElement('canvas');
  if (source instanceof ImageBitmap || source instanceof HTMLImageElement) {
    canvas.width =
      'naturalWidth' in source && source.naturalWidth ? source.naturalWidth : source.width;
    canvas.height =
      'naturalHeight' in source && source.naturalHeight ? source.naturalHeight : source.height;
  } else {
    canvas.width = source.width;
    canvas.height = source.height;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable.');
  ctx.drawImage(source as CanvasImageSource, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png'),
  );
  if (!blob) throw new Error('Could not convert source to blob.');
  return detectPoseVector(blob);
}
